import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  FormGroup,
} from "reactstrap";
import { saveProduct } from "../../components/Api/GmsProduct";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getTaxes } from "../../components/Api/GmsTax";
import { searchSuppliers } from "../../components/Api/GmsSupplier";
import { getActiveUnits } from "../../components/Api/GmsUnits";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  hasPagePermissionWithAdmin, 
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

const defaultForm = {
  product_Id: null,
  product_Code: "",
  product_NameEn: "",
  product_NameAr: "",
  product_Description: "",
  product_CostPrice: 0,
  product_Amount: 0,
  product_NetAmount: 0,
  product_Business_Id: null,
  product_Supplier_Id: null,
  product_Status: true,
  product_CreatedDate: new Date().toISOString(),
  product_CreatedBy: 1,
  product_ModifiedDate: new Date().toISOString(),
  product_ModifiedBy: 1,
  product_Number: "",
  product_DiscountPercentage: 0,
  taxIds: [],
  Unit_Id: 1,
  ExpiryRequired: false,
};

export default function AddProductPage() {
  const navigate = useNavigate();
  
  // Check permissions first
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsAddProduct');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsAddProduct');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsAddProduct');

  const [form, setForm] = useState(() => {
    const defaultStatus = hasDeletePermission ? defaultForm.product_Status : true;
    return { ...defaultForm, product_Status: defaultStatus };
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [businessOptions, setBusinessOptions] = useState([]);
  const [taxOptions, setTaxOptions] = useState([]);
  const [selectedTaxes, setSelectedTaxes] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [supplierSearchOptions, setSupplierSearchOptions] = useState([]);
  const [supplierSearchLoading, setSupplierSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedSupplierOption, setSelectedSupplierOption] = useState(null);

  // Session management
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
    };
  };

  useEffect(() => {
    if (hasPermission) {
      loadBusinesses();
      loadTaxes();
      loadUnits();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const loadBusinesses = async () => {
    try {
      const data = await getBusinesses();
      const options = data.map(business => ({
        value: business.business_Id,
        label: business.business_NameEn
      }));
      setBusinessOptions(options);
    } catch (error) {
      Swal.fire("Error", "Failed to load businesses: " + error.message, "error");
    }
  };

  const loadTaxes = async () => {
    try {
      const data = await getTaxes({ Tax_Active: true });
      const options = (data || []).map(tax => ({
        value: tax.tax_Id,
        label: `${tax.tax_NameEn} (${tax.tax_Perc}%)`
      }));
      setTaxOptions(options);
    } catch (error) {
      console.error("Error loading taxes:", error);
      setTaxOptions([]);
    }
  };

  const loadUnits = async () => {
    try {
      const data = await getActiveUnits();
      setUnitOptions(data || []);
    } catch (error) {
      console.error("Error loading units:", error);
      setUnitOptions([]);
    }
  };

  const searchSuppliersManually = async (inputValue) => {
    const trimmedInput = inputValue ? inputValue.trim() : '';
    
    if (!trimmedInput || trimmedInput.length < 3) {
      setSupplierSearchOptions([]);
      return;
    }
    
    try {
      setSupplierSearchLoading(true);
      const response = await searchSuppliers(trimmedInput, 1, 20);
      
      if (!response || !response.items || !Array.isArray(response.items)) {
        setSupplierSearchOptions([]);
        return;
      }
      
      const formattedOptions = response.items.map(supplier => {
        const hasArabicName = supplier.supplier_NameAr && supplier.supplier_NameAr.trim() !== '';
        const hasEnglishName = supplier.supplier_NameEn && supplier.supplier_NameEn.trim() !== '';
        const containsArabicChars = /[\u0600-\u06FF]/.test(trimmedInput);
        const searchInArabic = hasArabicName && (
          supplier.supplier_NameAr.includes(trimmedInput) || 
          (containsArabicChars && supplier.supplier_NameAr.includes(trimmedInput))
        );
        
        let primaryName, secondaryName;
        if (searchInArabic && hasArabicName) {
          primaryName = supplier.supplier_NameAr;
          secondaryName = hasEnglishName ? supplier.supplier_NameEn : null;
        } else if (hasEnglishName) {
          primaryName = supplier.supplier_NameEn;
          secondaryName = hasArabicName ? supplier.supplier_NameAr : null;
        } else if (hasArabicName) {
          primaryName = supplier.supplier_NameAr;
          secondaryName = null;
        } else {
          primaryName = 'N/A';
          secondaryName = null;
        }
        
        let label = primaryName;
        if (secondaryName && secondaryName.trim() !== '') {
          label = `${primaryName} — ${secondaryName}`;
        }
        label += ` — ${supplier.supplier_Code || 'N/A'}`;
        
        return {
          value: supplier.supplier_Id,
          label: label,
          supplier: supplier
        };
      });
      
      setSupplierSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching suppliers:', error);
      setSupplierSearchOptions([]);
    } finally {
      setSupplierSearchLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    if (field === "product_Code") {
      value = value.replace(/\s/g, '').substring(0, 10);
    }
    
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTaxChange = (selected) => {
    setSelectedTaxes(selected || []);
    const taxIds = selected ? selected.map(tax => tax.value) : [];
    setForm(prev => ({
      ...prev,
      taxIds: taxIds
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.product_Business_Id) {
      Swal.fire("Error", "Please select a business", "error");
      return;
    }
    
    if (!form.product_Supplier_Id) {
      Swal.fire("Error", "Please select a supplier", "error");
      return;
    }
    
    if (!form.product_NameEn.trim()) {
      Swal.fire("Error", "Please enter product name in English", "error");
      return;
    }

    if (form.product_Code && form.product_Code.length > 10) {
      Swal.fire("Error", "Product Code cannot exceed 10 characters", "error");
      return;
    }

    if (!form.Unit_Id) {
      Swal.fire("Error", "Please select a unit", "error");
      return;
    }

    setSaveLoading(true);
    try {
      const session = getUserSession();
      
      const productData = {
        ...form,
        product_CreatedBy: session.userId,
        product_ModifiedBy: session.userId,
        product_CreatedDate: new Date().toISOString(),
        product_ModifiedDate: new Date().toISOString(),
        taxIds: selectedTaxes.map(tax => tax.value),
        ExpiryRequired: form.ExpiryRequired !== undefined ? form.ExpiryRequired : false,
      };
      
      if (!productData.product_Id || productData.product_Id === 0) {
        delete productData.product_Id;
      }
      
      if (!productData.product_Business_Id || productData.product_Business_Id === 0) {
        delete productData.product_Business_Id;
      }

      const result = await saveProduct(productData);
      
      if (result) {
        Swal.fire("Success", "Product saved successfully!", "success");
        handleClear();
      }
    } catch (error) {
      let errorMessage = "Failed to save product";
      
      if (error.response && error.response.data) {
        if (typeof error.response.data === 'string') {
          const match = error.response.data.match(/System\.InvalidOperationException:\s*(.+?)(?:\s+at\s+|$)/);
          errorMessage = match ? match[1].trim() : error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleClear = () => {
    const defaultStatus = hasDeletePermission ? defaultForm.product_Status : true;
    setForm({ ...defaultForm, product_Status: defaultStatus });
    setSelectedTaxes([]);
    setSelectedSupplierOption(null);
  };

  const selectedBusiness = businessOptions.find(b => b.value === form.product_Business_Id);

  if (!hasPermission || !hasEditOrHigher) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Add Product" breadcrumbItem="Spa" />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 className="card-title mb-0">Add New Product</h4>
                  <Button color="secondary" onClick={() => navigate('/GmsProductManagement')}>
                    <i className="bx bx-arrow-back me-1"></i>
                    Back to Product List
                  </Button>
                </div>

                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <FormGroup className="mb-4">
                        <Label for="product_Business_Id">Company <span className="text-danger">*</span></Label>
                        <Select
                          id="product_Business_Id"
                          value={selectedBusiness || null}
                          onChange={(selected) => handleFormChange("product_Business_Id", selected?.value || 0)}
                          options={businessOptions}
                          placeholder="Select Company"
                          isClearable
                          isSearchable
                          maxMenuHeight={80}
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: '38px',
                              border: '1px solid #ced4da',
                              borderRadius: '0.375rem',
                              '&:hover': {
                                borderColor: '#adb5bd'
                              },
                              '&:focus': {
                                borderColor: '#80bdff',
                                boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)'
                              }
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : 'white',
                              color: state.isSelected ? 'white' : '#495057',
                              padding: '8px 12px',
                              cursor: 'pointer'
                            })
                          }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup className="mb-4">
                        <Label for="product_Supplier_Id">Supplier <span className="text-danger">*</span></Label>
                        <Select
                          value={selectedSupplierOption}
                          onChange={(option) => {
                            setSelectedSupplierOption(option);
                            handleFormChange('product_Supplier_Id', option?.value || null);
                          }}
                          options={supplierSearchOptions}
                          placeholder="Search by name..."
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: '38px',
                              height: '38px',
                              border: '1px solid #ced4da',
                              borderRadius: '0.375rem',
                              '&:hover': {
                                borderColor: '#adb5bd'
                              },
                              '&:focus': {
                                borderColor: '#80bdff',
                                boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)'
                              }
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : 'white',
                              color: state.isSelected ? 'white' : '#495057',
                              padding: '8px 12px',
                              cursor: 'pointer'
                            })
                          }}
                          isClearable
                          isSearchable
                          isLoading={supplierSearchLoading}
                          noOptionsMessage={() => "Type at least 3 characters"}
                          loadingMessage={() => "Searching..."}
                          filterOption={null}
                          onInputChange={(inputValue) => {
                            if (searchTimeout) {
                              clearTimeout(searchTimeout);
                            }
                            
                            const trimmedValue = inputValue ? inputValue.trim() : '';
                            
                            const timeout = setTimeout(() => {
                              if (trimmedValue.length >= 3) {
                                searchSuppliersManually(trimmedValue);
                              } else {
                                setSupplierSearchOptions([]);
                              }
                            }, 300);
                            setSearchTimeout(timeout);
                            
                            return inputValue;
                          }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <FormGroup className="mb-4">
                        <Label for="product_Code">Product Code (Optional)</Label>
                        <Input
                          type="text"
                          id="product_Code"
                          value={form.product_Code}
                          onChange={(e) => handleFormChange("product_Code", e.target.value)}
                          placeholder="Product Code"
                          maxLength="10"
                        />
                        <small className="text-muted mt-2 d-block">
                          Maximum 10 characters, no spaces allowed
                        </small>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup className="mb-4">
                        <Label for="product_NameEn">Product Name (English) <span className="text-danger">*</span></Label>
                        <Input
                          type="text"
                          id="product_NameEn"
                          value={form.product_NameEn}
                          onChange={(e) => handleFormChange("product_NameEn", e.target.value)}
                          placeholder="Enter product name in English"
                          required
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <FormGroup className="mb-4">
                        <Label for="product_NameAr">Product Name (Arabic)</Label>
                        <Input
                          type="text"
                          id="product_NameAr"
                          value={form.product_NameAr}
                          onChange={(e) => handleFormChange("product_NameAr", e.target.value)}
                          placeholder="Enter product name in Arabic"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <FormGroup className="mb-4">
                        <Label for="product_TaxIds">Taxes</Label>
                        <Select
                          id="product_TaxIds"
                          value={selectedTaxes}
                          onChange={handleTaxChange}
                          options={taxOptions}
                          isMulti
                          placeholder="Select Taxes"
                          isClearable
                          maxMenuHeight={200}
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: '38px',
                              border: '1px solid #ced4da',
                              borderRadius: '0.375rem',
                              '&:hover': {
                                borderColor: '#adb5bd'
                              },
                              '&:focus': {
                                borderColor: '#80bdff',
                                boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)'
                              }
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : 'white',
                              color: state.isSelected ? 'white' : '#495057',
                              padding: '8px 12px',
                              cursor: 'pointer'
                            })
                          }}
                        />
                        <small className="text-muted mt-2 d-block">
                          Select one or more taxes to apply to this product
                        </small>
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <FormGroup className="mb-4">
                        <Label for="product_Description">Description (Optional)</Label>
                        <Input
                          type="textarea"
                          id="product_Description"
                          rows="3"
                          value={form.product_Description}
                          onChange={(e) => handleFormChange("product_Description", e.target.value)}
                          placeholder="Enter product description"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <FormGroup className="mb-4">
                        <Label for="product_Unit_Id">Unit <span className="text-danger">*</span></Label>
                        <Select
                          id="product_Unit_Id"
                          value={unitOptions.find(option => Number(option.value) === Number(form.Unit_Id)) || null}
                          onChange={(selectedOption) => handleFormChange("Unit_Id", selectedOption ? Number(selectedOption.value) : null)}
                          options={unitOptions}
                          placeholder="Select unit"
                          isClearable={false}
                          isSearchable={true}
                          className="react-select-container"
                          classNamePrefix="react-select"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup className="mb-4">
                        <Label>Expiry Date Required</Label>
                        <div className="d-flex gap-3 mt-2">
                          <div className="form-check">
                            <Input
                              type="radio"
                              id="expiry_yes"
                              name="ExpiryRequired"
                              checked={form.ExpiryRequired === true}
                              onChange={() => handleFormChange("ExpiryRequired", true)}
                              className="form-check-input"
                            />
                            <Label for="expiry_yes" className="form-check-label">
                              Yes
                            </Label>
                          </div>
                          <div className="form-check">
                            <Input
                              type="radio"
                              id="expiry_no"
                              name="ExpiryRequired"
                              checked={form.ExpiryRequired === false}
                              onChange={() => handleFormChange("ExpiryRequired", false)}
                              className="form-check-input"
                            />
                            <Label for="expiry_no" className="form-check-label">
                              No
                            </Label>
                          </div>
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>
                  {hasDeletePermission && (
                    <Row>
                      <Col md={12}>
                        <FormGroup className="mb-4">
                          <div className="form-check form-switch">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="product_Status"
                              checked={form.product_Status}
                              onChange={(e) => handleFormChange("product_Status", e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="product_Status">
                              {form.product_Status ? "Active" : "Inactive"}
                            </label>
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                  )}
                  <Row>
                    <Col md={12}>
                      <div className="d-flex justify-content-end">
                        <Button color="primary" type="submit" disabled={saveLoading} className="me-2">
                          {saveLoading ? <ClipLoader size={20} color="white" /> : "Save"}
                        </Button>
                        <Button color="secondary" type="button" onClick={handleClear}>
                          Clear
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

